import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { MallCategory } from "../api/types";

/**
 * Round 30 point 2 — Mall categories are entirely admin-defined (Frames,
 * Cars, Entrance, Chat Bubble, Bag, PNG, SVGA, or anything else you want
 * to call them) — never a hardcoded enum anywhere in the app or backend.
 * The Android Mall tab's category tabs read this table directly, in
 * sort_order.
 */
export function MallCategoriesPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<MallCategory>("/admin/mall-categories");
  const [editing, setEditing] = useState<Partial<MallCategory> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  async function save() {
    if (!editing?.name) {
      showToast("Name is required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const body = { name: editing.name, sort_order: Number(editing.sort_order) || 0, is_active: editing.is_active ?? true };
      if (editing.id) {
        await apiRequest(`/admin/mall-categories/${editing.id}`, { method: "PATCH", body });
      } else {
        await apiRequest("/admin/mall-categories", { method: "POST", body });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save category", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function toggleActive(row: MallCategory) {
    try {
      await apiRequest(`/admin/mall-categories/${row.id}`, { method: "PATCH", body: { is_active: !row.is_active } });
      showToast(row.is_active ? "Deactivated" : "Activated", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update category", "error");
    }
  }

  async function remove(row: MallCategory) {
    try {
      await apiRequest(`/admin/mall-categories/${row.id}`, { method: "DELETE" });
      showToast("Deleted", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete category", "error");
    }
  }

  const columns: Column<MallCategory>[] = [
    { header: "Name", render: (row) => <p className="font-medium text-ink">{row.name}</p> },
    { header: "Sort Order", render: (row) => row.sort_order },
    { header: "Status", render: (row) => (row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>) },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>Edit</Button>
          <Button size="sm" variant={row.is_active ? "danger" : "secondary"} onClick={() => toggleActive(row)}>
            {row.is_active ? "Deactivate" : "Activate"}
          </Button>
          <Button size="sm" variant="danger" onClick={() => remove(row)}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Mall Categories">
      <p className="mb-4 text-xs text-ink-faint">
        Mall Items (the other Mall page) pick one of these categories — deleting a category here doesn't delete
        any item or purchase that referenced it, it just leaves that item uncategorized.
      </p>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setEditing({ is_active: true, sort_order: 0 })}>+ Add Category</Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No Mall categories yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Category" : "Add Category"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} isLoading={isSubmitting}>Save</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Name" value={editing?.name ?? ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} />
          <Input
            label="Sort order"
            type="number"
            value={editing?.sort_order ?? 0}
            onChange={(e) => setEditing((p) => ({ ...p, sort_order: Number(e.target.value) }))}
          />
        </div>
      </Modal>
    </AppShell>
  );
}
